/**
 * Transfers List Page - Clara Vale V2
 *
 * Compact layout mirroring Registrations: ListPageHeader (title + subtitle
 * + time filter) + single-row ListPageToolbar with status pills, saved
 * views, column toggle, search, and export. All shared components - no
 * bespoke header/toolbar/filter code.
 */

import { useState, useMemo, useCallback } from 'react'
import { useNavigate } from 'react-router'
import { useTranslation } from 'react-i18next'
import { AppShell } from '@/admin/components/app-shell'
import {
  useTransfers,
  useTransferStatusCounts,
  type Transfer,
} from '@/admin/hooks/use-transfers'
import { useTableState } from '@/admin/hooks/use-table-state'
import { useTableColumnPreferences } from '@/admin/hooks/use-table-column-preferences'
import { useDismissedStatusPills } from '@/admin/hooks/use-dismissed-status-pills'
import { EmptyState } from '@/admin/components/shared/empty-state'
import { ArrowLeftRight } from 'lucide-react'
import {
  DataTable,
  type DataTableColumn,
} from '@/admin/components/data-table'
import {
  useTableViews,
  useCreateTableView,
  useDeleteTableView,
  useUpdateTableView,
  useSetOrgDefaultView,
} from '@/admin/hooks/use-table-views'
import { ListPageToolbar } from '@/admin/components/list-page-toolbar'
import { ListPageHeader } from '@/admin/components/shared/list-page-header'
import { StatusBadge } from '@/admin/components/shared/status-badge'
import { NotificationDot } from '@/shared/components/notifications/notification-dot'
import { useTableExport } from '@/admin/hooks/use-table-export'
import { useDebouncedValue } from '@/shared/hooks/use-debounced-value'
import { useFormatters } from '@/shared/lib/formatting'
import type { TableViewConfig, TableView } from '@/admin/types/table-views'
import type { StatusPillItem } from '@/admin/components/shared/status-pills'
import type { TimeRange } from '@/admin/components/shared/time-filter'
import { timeRangeToParams } from '@/admin/lib/time-range-params'

const TABLE_ID = 'transfers'

/** Only the reference column is structural; everything else is hideable. */
const LOCK_CANDIDATE_IDS = ['new_owner']

/** Format a local Date as YYYY-MM-DD without the UTC shift that
 *  ``toISOString()`` introduces for negative-offset timezones (a US user's
 *  local "today" would otherwise serialize as tomorrow, or a boundary date
 *  as the previous day). */
export function TransfersPage() {
  const navigate = useNavigate()
  const { t } = useTranslation()
  const { formatDate } = useFormatters()
  const [search, setSearch] = useState('')
  const [activeViewUuid, setActiveViewUuid] = useState<string | null>(null)
  const [statusFilter, setStatusFilter] = useState<string | null>(null)
  const [timeRange, setTimeRange] = useState<TimeRange>('all_time')
  const [customRange, setCustomRange] = useState<{ from: string; to: string } | undefined>()

  const {
    columnVisibility,
    setColumnVisibility,
    columnOrder,
    setColumnOrder,
    saveColumnVisibility,
    saveColumnOrder,
    applyDefaultColumnPreferences,
  } = useTableColumnPreferences(TABLE_ID)
  const { dismissedKeys: dismissedStatusKeys, dismiss: dismissStatusPill } =
    useDismissedStatusPills(TABLE_ID)

  const { page, pageSize, sorting, setPage, setPageSize, setSorting, queryParams } =
    useTableState(TABLE_ID)

  // Views
  const { data: views = [] } = useTableViews(TABLE_ID)
  const createView = useCreateTableView()
  const deleteView = useDeleteTableView()
  const updateView = useUpdateTableView()
  const setOrgDefault = useSetOrgDefaultView()

  const debouncedSearch = useDebouncedValue(search)

  // Params shared by the list and the status-counts endpoints. The counts
  // endpoint ignores ``status`` so clicking a pill filters the table
  // without collapsing the pill bar.
  const paramsForCounts = useMemo(() => {
    const p: Record<string, string> = { ...timeRangeToParams(timeRange, customRange) }
    if (debouncedSearch) p.search = debouncedSearch
    return p
  }, [debouncedSearch, timeRange, customRange])

  const params = useMemo(() => {
    const p: Record<string, string> = { ...queryParams, ...paramsForCounts }
    if (statusFilter) p.status = statusFilter
    return p
  }, [queryParams, paramsForCounts, statusFilter])

  const { data, isLoading, isFetching, isError, refetch } = useTransfers(params)

  const results = useMemo(() => data?.results ?? [], [data])
  const totalCount = data?.count ?? 0

  const { data: statusCounts = [] } = useTransferStatusCounts(paramsForCounts)
  const statusPills: StatusPillItem[] = useMemo(
    () =>
      statusCounts.map((s) => ({ key: s.key, label: s.label, count: s.count })),
    [statusCounts],
  )
  const needsReviewCount = statusCounts.find((s) => s.key === 'pending_review')?.count

  const handleTimeFilterChange = useCallback(
    (range: TimeRange, custom?: { from: string; to: string }) => {
      setTimeRange(range)
      setCustomRange(custom)
      setPage(1)
    },
    [setPage],
  )

  const columns: DataTableColumn<Transfer>[] = useMemo(
    () => [
      {
        id: 'new_owner',
        header: 'New Owner',
        cell: (row) => (
          <span className="inline-flex items-center gap-1.5">
            {row.has_unread_activity && (
              <NotificationDot level="attention" title="Unread activity" />
            )}
            {`${row.new_owner_first_name} ${row.new_owner_last_name}`.trim() || '-'}
          </span>
        ),
        sortable: true,
        sortField: 'new_owner_last_name',
      },
      {
        id: 'property',
        header: 'Property',
        cell: (row) => row.host_address?.display || '-',
      },
      {
        id: 'status',
        header: 'Status',
        cell: (row) => <StatusBadge status={row.status_label ?? row.status} />,
        sortable: true,
        sortField: 'status',
      },
      {
        id: 'transfer_type',
        header: 'Transfer Type',
        cell: (row) => row.transfer_type?.label ?? '-',
        sortable: true,
        sortField: 'transfer_type__code',
      },
      {
        id: 'ownership_change_date',
        header: 'Ownership Change',
        cell: (row) =>
          row.ownership_change_date ? formatDate(row.ownership_change_date) : '-',
        sortable: true,
        sortField: 'ownership_change_date',
      },
      {
        id: 'updated_at',
        header: 'Last Activity',
        cell: (row) => formatDate(row.updated_at),
        sortable: true,
        sortField: 'updated_at',
      },
    ],
    [formatDate],
  )

  const { exportCsv, isExporting } = useTableExport<Transfer>({
    endpoint: '/v1/transfers/',
    columns,
    columnVisibility,
    params,
  })

  function buildCurrentConfig(
    overrides: { columnVisibility?: Record<string, boolean>; columnOrder?: string[] } = {},
  ): TableViewConfig {
    const nextColumnVisibility = overrides.columnVisibility ?? columnVisibility
    const nextColumnOrder = overrides.columnOrder ?? columnOrder
    const visibleIds = columns
      .filter((col) => {
        if (col.id in nextColumnVisibility) return nextColumnVisibility[col.id] !== false
        return col.defaultVisible !== false
      })
      .map((col) => col.id)
    return {
      columns: visibleIds,
      columnOrder: nextColumnOrder,
      sorting: sorting ?? null,
      filters: [],
    }
  }

  const currentConfig = buildCurrentConfig()

  const alwaysShownColumns = useMemo(() => {
    return LOCK_CANDIDATE_IDS.filter((id) => {
      const col = columns.find((c) => c.id === id)
      if (!col) return false
      if (id in columnVisibility) return columnVisibility[id] !== false
      return col.defaultVisible !== false
    })
  }, [columns, columnVisibility])

  const handleSelectView = useCallback(
    (view: TableView | null) => {
      setActiveViewUuid(view?.uuid ?? null)
      if (view?.config) {
        if (view.config.columns) {
          const vis: Record<string, boolean> = {}
          columns.forEach((c) => {
            vis[c.id] = c.id === 'new_owner' || view.config.columns!.includes(c.id)
          })
          setColumnVisibility(vis)
        } else {
          setColumnVisibility({})
        }
        setColumnOrder(view.config.columnOrder ?? [])
        setSorting(view.config.sorting ?? null)
      } else {
        applyDefaultColumnPreferences()
        setSorting(null)
      }
    },
    [applyDefaultColumnPreferences, columns, setColumnOrder, setSorting, setColumnVisibility],
  )

  return (
    <AppShell>
      <main className="page-container py-10">
        <ListPageHeader
          title="Transfers"
          totalCount={totalCount}
          highlightStat={
            needsReviewCount ? { label: 'need review', value: needsReviewCount } : undefined
          }
          timeFilterValue={timeRange}
          timeFilterCustomRange={customRange}
          onTimeFilterChange={handleTimeFilterChange}
          actionButton={{
            label: 'New Transfer',
            onClick: () => navigate('/transfers/new'),
            dataTour: 'transfers-list',
          }}
        />

        <ListPageToolbar<Transfer>
          layout="compact"
          tableId={TABLE_ID}
          search={search}
          onSearchChange={(v) => {
            setSearch(v)
            setPage(1)
          }}
          searchPlaceholder="Search transfers..."
          statusPills={statusPills}
          activeStatusKey={statusFilter}
          onStatusSelect={(key) => {
            setStatusFilter(key)
            setPage(1)
          }}
          statusTotalCount={totalCount}
          dismissedStatusKeys={dismissedStatusKeys}
          onStatusDismiss={(key) => {
            dismissStatusPill(key)
            if (statusFilter === key) {
              setStatusFilter(null)
              setPage(1)
            }
          }}
          views={views}
          activeViewUuid={activeViewUuid}
          onSelectView={handleSelectView}
          onSaveView={(name) => createView.mutate({ table_id: TABLE_ID, name, config: currentConfig })}
          onDeleteView={(uuid) => {
            deleteView.mutate({ uuid, tableId: TABLE_ID })
            if (activeViewUuid === uuid) setActiveViewUuid(null)
          }}
          onUpdateView={(uuid) => updateView.mutate({ uuid, tableId: TABLE_ID, config: currentConfig })}
          onRenameView={(uuid, name) => updateView.mutate({ uuid, tableId: TABLE_ID, name })}
          onSetOrgDefault={() => setOrgDefault.mutate({ table_id: TABLE_ID, name: 'Default', config: currentConfig })}
          currentConfig={currentConfig}
          columns={columns}
          activeFilters={[]}
          onFiltersChange={() => {}}
          showColumnFilter={false}
          columnVisibility={columnVisibility}
          onColumnVisibilityChange={(visibility) => {
            setColumnVisibility(visibility)
            if (activeViewUuid) {
              updateView.mutate({
                uuid: activeViewUuid,
                tableId: TABLE_ID,
                config: buildCurrentConfig({ columnVisibility: visibility }),
              })
            } else {
              saveColumnVisibility(visibility)
            }
          }}
          alwaysShownColumns={alwaysShownColumns}
          columnOrder={columnOrder}
          onColumnOrderChange={(order) => {
            setColumnOrder(order)
            if (activeViewUuid) {
              updateView.mutate({
                uuid: activeViewUuid,
                tableId: TABLE_ID,
                config: buildCurrentConfig({ columnOrder: order }),
              })
            } else {
              saveColumnOrder(order)
            }
          }}
          onExport={exportCsv}
          isExporting={isExporting}
        />

        <div data-tour="transfer-list">
          <DataTable<Transfer>
            tableId={TABLE_ID}
            columns={columns}
            data={results}
            totalCount={totalCount}
            isLoading={isLoading}
            isFetching={isFetching}
            isError={isError}
            onRetry={refetch}
            page={page}
            pageSize={pageSize}
            onPageChange={setPage}
            onPageSizeChange={setPageSize}
            sorting={sorting}
            columnOrder={columnOrder}
            onSortingChange={setSorting}
            onRowClick={(row) => navigate(`/transfers/${row.uuid}`)}
            emptyStateNode={
              <EmptyState
                icon={ArrowLeftRight}
                title={t('transfers.emptyTitle', 'No transfers yet')}
                description={t(
                  'transfers.emptyDescription',
                  'Warranty ownership transfers will appear here.',
                )}
              />
            }
            columnVisibility={columnVisibility}
            highlightQuery={debouncedSearch}
            rowKey="uuid"
          />
        </div>
      </main>
    </AppShell>
  )
}
